import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../baseQuery';
import { SortOrder } from 'types/interfaces/sort';
import {
  CardAddSource,
  CollectionListFetchRequest,
  CollectionListFetchResponse,
} from '../types';
import { CollectionList } from 'types/interfaces/collectionList';
import { CardCondition } from 'types/interfaces/condition/condition';
import { tradeApi } from '../../trade/tradeApi';
import { settingsApi } from '../../settings/settingsApi';

export interface HaveListFetchResponse extends CollectionListFetchResponse {
  haveList: CollectionList[];
}

export interface AddCardToHaveListRequest {
  cardId: number;
  quantity?: number;
  condition?: CardCondition;
  foil?: boolean;
  source?: CardAddSource; // telemetry: which surface this add came from
}

export interface UpdateHaveListCardRequest {
  haveListRecordId: number;
  newCardId?: number;
  quantity?: number;
  condition?: CardCondition;
  foil?: boolean;
  forTrade?: boolean;
  newCardScryfallId?: string;
}

export interface HaveListBulkImportTextRequest {
  text: string;
}

export interface HaveListBulkImportResponse {
  totalRows: number;
  successCount: number;
  errorCount: number;
  errors: Array<{
    row: number;
    cardName?: string;
    error: string;
  }>;
  importedCards: Array<{
    id: number;
    cardName: string;
    quantity: number;
  }>;
}

export interface AddSetToHaveListRequest {
  setCodes: string[];
  quantity?: number;
  condition?: string;
  foil?: boolean;
  forTrade?: boolean;
  skipExisting?: boolean;
  skipBasicLands?: boolean;
  rarities?: string[];
}

export interface AddSetToHaveListResponse {
  success: boolean;
  totalCardsProcessed: number;
  cardsAdded: number;
  cardsSkipped: number;
  errors: Array<{ setCode: string; cardName?: string; error: string }>;
  sets: Array<{ setCode: string; setName: string; cardsAdded: number; cardsSkipped: number }>;
  message: string;
}

export const haveListApi = createApi({
  reducerPath: 'haveListApi',
  baseQuery: baseQuery,
  tagTypes: ['HaveList'],
  endpoints: (builder) => ({
    haveListFetch: builder.infiniteQuery<
      HaveListFetchResponse,
      CollectionListFetchRequest,
      number,
      string
    >({
      query: ({ pageParam, queryArg }) => {
        // Build URL parameters conditionally to avoid sending empty values
        const params = new URLSearchParams({
          page: pageParam.toString(),
          limit: '10'
        });

        // Only add sort if it has a value
        if (queryArg.sort) {
          params.append('sort', queryArg.sort);
        }

        // Only add direction if it has a value (not undefined or empty string)
        // This fixes the issue where empty string parameters were causing backend parsing errors
        if (queryArg.direction) {
          params.append('direction', queryArg.direction);
        }

        return {
          url: `/have-list/me?${params.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
          return lastPage.hasMore ? lastPage.page + 1 : undefined;
        },
      },
      providesTags: ['HaveList'],
    }),
    haveListFetchPaginated: builder.query<
      HaveListFetchResponse,
      { page: number; sort?: string; direction?: SortOrder; search?: string; forTrade?: boolean }
    >({
      query: ({ page, sort, direction, search, forTrade }) => {
        // Build URL parameters conditionally to avoid sending empty values
        const params = new URLSearchParams({
          page: page.toString(),
          limit: '100' // 100 cards per page
        });

        // Only add sort if it has a value
        if (sort) {
          params.append('sort', sort);
        }

        // Only add direction if it has a value
        if (direction) {
          params.append('direction', direction);
        }

        // Only add search if it has a value
        if (search) {
          params.append('search', search);
        }

        // Only add forTrade filter if explicitly set
        if (forTrade !== undefined) {
          params.append('forTrade', forTrade.toString());
        }

        return {
          url: `/have-list/me?${params.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      providesTags: ['HaveList'],
    }),
    addCardToHaveList: builder.mutation<void, AddCardToHaveListRequest>({
      query: (body) => ({
        url: '/have-list/add-card',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['HaveList'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
        } catch {}
      },
    }),
    cardToHaveListQuantityUpdate: builder.mutation<
      void,
      { haveListRecordId: number; change: number; sort?: string; direction?: SortOrder }
    >({
      query: (body) => ({
        url: '/have-list/card/quantity-update',
        method: 'PATCH',
        body,
        requiresAuth: true,
      }),
      async onQueryStarted(
        { haveListRecordId, change, sort, direction },
        { dispatch, queryFulfilled, getState }
      ) {
        // Optimistic update for infinite query
        const patchResult = dispatch(
          haveListApi.util.updateQueryData(
            'haveListFetch',
            { sort, direction },
            (draft) => {
              draft.pages.forEach((page) => {
                page.totalCards = page.totalCards + change;
                page.haveList = page.haveList.map((item) =>
                  item.id === haveListRecordId
                    ? { ...item, quantity: item.quantity + change }
                    : item
                );
              });
            }
          )
        );

        // Optimistic update for paginated query - find all cached pages
        const paginatedPatches: any[] = [];
        const state = getState() as any;
        const queries = state.haveListApi?.queries || {};

        Object.keys(queries).forEach((key) => {
          if (key.startsWith('haveListFetchPaginated')) {
            const query = queries[key];
            if (query?.data) {
              const args = query.originalArgs;
              const paginatedPatch = dispatch(
                haveListApi.util.updateQueryData(
                  'haveListFetchPaginated',
                  args,
                  (draft) => {
                    draft.haveList = draft.haveList.map((item) =>
                      item.id === haveListRecordId
                        ? { ...item, quantity: item.quantity + change }
                        : item
                    );
                    // Update total cards count
                    draft.totalCards = draft.totalCards + change;
                  }
                )
              );
              paginatedPatches.push(paginatedPatch);
            }
          }
        });

        try {
          await queryFulfilled;
          // Invalidate match index cache so Send page gets fresh auto-match data
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
        } catch (e) {
          // Roll back all optimistic updates on failure
          patchResult.undo();
          paginatedPatches.forEach(patch => patch.undo());
        }
      },
    }),
    removeCardFromHaveList: builder.mutation<
      void,
      { haveListRecordId: number; sort?: string; direction?: SortOrder }
    >({
      query: (body) => ({
        url: '/have-list/remove-card',
        method: 'DELETE',
        body: { haveListRecordId: body.haveListRecordId },
        requiresAuth: true,
      }),
      // Invalidate tags to refresh both infinite and paginated queries
      invalidatesTags: ['HaveList'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
        } catch {}
      },
    }),
    updateHaveListCard: builder.mutation<void, UpdateHaveListCardRequest>({
      query: (body) => ({
        url: '/have-list/card/update',
        method: 'PATCH',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['HaveList'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
        } catch {}
      },
    }),
    toggleTradeStatus: builder.mutation<
      void,
      { haveListRecordId: number; forTrade: boolean }
    >({
      query: (body) => ({
        url: '/have-list/card/trade-status',
        method: 'PATCH',
        body,
        requiresAuth: true,
      }),
      async onQueryStarted(
        { haveListRecordId, forTrade },
        { dispatch, queryFulfilled, getState }
      ) {
        // Optimistic update for infinite query
        const infinitePatch = dispatch(
          haveListApi.util.updateQueryData(
            'haveListFetch',
            {},
            (draft) => {
              draft.pages.forEach((page) => {
                page.haveList = page.haveList.map((item) =>
                  item.id === haveListRecordId
                    ? { ...item, forTrade }
                    : item
                );
              });
            }
          )
        );

        // Optimistic update for all cached paginated queries
        const paginatedPatches: any[] = [];
        const state = getState() as any;
        const queries = state.haveListApi?.queries || {};

        Object.keys(queries).forEach((key) => {
          if (key.startsWith('haveListFetchPaginated')) {
            const query = queries[key];
            if (query?.data) {
              const args = query.originalArgs;
              const paginatedPatch = dispatch(
                haveListApi.util.updateQueryData(
                  'haveListFetchPaginated',
                  args,
                  (draft) => {
                    draft.haveList = draft.haveList.map((item) =>
                      item.id === haveListRecordId
                        ? { ...item, forTrade }
                        : item
                    );
                  }
                )
              );
              paginatedPatches.push(paginatedPatch);
            }
          }
        });

        try {
          await queryFulfilled;
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
        } catch {
          // Roll back all optimistic updates on failure
          infinitePatch.undo();
          paginatedPatches.forEach(patch => patch.undo());
        }
      },
    }),
    toggleFoilStatus: builder.mutation<
      void,
      { haveListRecordId: number; foil: boolean }
    >({
      query: ({ haveListRecordId, foil }) => ({
        url: '/have-list/card/update',
        method: 'PATCH',
        body: { haveListRecordId, foil },
        requiresAuth: true,
      }),
      // Foil toggle can trigger backend row merges that change the authoritative row id.
      // Refetch HaveList after success so the cache picks up the new id instead of holding
      // a ghost id that the next click would 404 on.
      invalidatesTags: ['HaveList'],
      async onQueryStarted(
        { haveListRecordId, foil },
        { dispatch, queryFulfilled, getState }
      ) {
        const infinitePatch = dispatch(
          haveListApi.util.updateQueryData(
            'haveListFetch',
            {},
            (draft) => {
              draft.pages.forEach((page) => {
                page.haveList = page.haveList.map((item) =>
                  item.id === haveListRecordId
                    ? { ...item, foil }
                    : item
                );
              });
            }
          )
        );

        const paginatedPatches: any[] = [];
        const state = getState() as any;
        const queries = state.haveListApi?.queries || {};

        Object.keys(queries).forEach((key) => {
          if (key.startsWith('haveListFetchPaginated')) {
            const query = queries[key];
            if (query?.data) {
              const args = query.originalArgs;
              const paginatedPatch = dispatch(
                haveListApi.util.updateQueryData(
                  'haveListFetchPaginated',
                  args,
                  (draft) => {
                    draft.haveList = draft.haveList.map((item) =>
                      item.id === haveListRecordId
                        ? { ...item, foil }
                        : item
                    );
                  }
                )
              );
              paginatedPatches.push(paginatedPatch);
            }
          }
        });

        try {
          await queryFulfilled;
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
        } catch {
          infinitePatch.undo();
          paginatedPatches.forEach(patch => patch.undo());
        }
      },
    }),
    toggleAlertEnabled: builder.mutation<
      void,
      { haveListRecordId: number; alertEnabled: boolean }
    >({
      query: (body) => ({
        url: '/have-list/card/alert-enabled',
        method: 'PATCH',
        body,
        requiresAuth: true,
      }),
      async onQueryStarted(
        { haveListRecordId, alertEnabled },
        { dispatch, queryFulfilled, getState }
      ) {
        const infinitePatch = dispatch(
          haveListApi.util.updateQueryData(
            'haveListFetch',
            {},
            (draft) => {
              draft.pages.forEach((page) => {
                page.haveList = page.haveList.map((item) =>
                  item.id === haveListRecordId
                    ? { ...item, alertEnabled }
                    : item
                );
              });
            }
          )
        );

        const paginatedPatches: any[] = [];
        const state = getState() as any;
        const queries = state.haveListApi?.queries || {};

        Object.keys(queries).forEach((key) => {
          if (key.startsWith('haveListFetchPaginated')) {
            const query = queries[key];
            if (query?.data) {
              const args = query.originalArgs;
              const paginatedPatch = dispatch(
                haveListApi.util.updateQueryData(
                  'haveListFetchPaginated',
                  args,
                  (draft) => {
                    draft.haveList = draft.haveList.map((item) =>
                      item.id === haveListRecordId
                        ? { ...item, alertEnabled }
                        : item
                    );
                  }
                )
              );
              paginatedPatches.push(paginatedPatch);
            }
          }
        });

        try {
          await queryFulfilled;
          dispatch(settingsApi.util.invalidateTags(['HaveListDefaults']));
        } catch {
          infinitePatch.undo();
          paginatedPatches.forEach(patch => patch.undo());
        }
      },
    }),
    deleteAllHaveListItems: builder.mutation<{ message: string; deletedCount: number }, void>({
      query: () => ({
        url: '/have-list/delete-all',
        method: 'DELETE',
        requiresAuth: true,
      }),
      invalidatesTags: ['HaveList'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
        } catch {}
      },
    }),
    haveListQuantitySummary: builder.query<{ quantities: Record<string, number>; quantitiesByCardId: Record<string, number> }, void>({
      query: () => ({
        url: '/have-list/quantity-summary',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['HaveList'],
    }),
    bulkImportHaveList: builder.mutation<HaveListBulkImportResponse, FormData | HaveListBulkImportTextRequest>({
      query: (body) => ({
        url: '/have-list/bulk-import',
        method: 'POST',
        body,
        requiresAuth: true,
        timeout: 300000, // 5 minute timeout for large imports
      }),
      invalidatesTags: ['HaveList'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
        } catch {}
      },
    }),
    exportHaveList: builder.query<
      { items: any[]; truncated: boolean },
      { search?: string; forTrade?: boolean }
    >({
      query: ({ search, forTrade }) => {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        if (forTrade !== undefined) params.append('forTrade', forTrade.toString());
        const qs = params.toString();
        return {
          url: `/have-list/export${qs ? `?${qs}` : ''}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
    }),
    batchUpdateHaveList: builder.mutation<
      { successCount: number; totalCount: number; failures: { id: number; error: string }[] },
      { ids: number[]; changes: { condition?: CardCondition; foil?: boolean; forTrade?: boolean; alertEnabled?: boolean } }
    >({
      query: (body) => ({
        url: '/have-list/batch-update',
        method: 'PATCH',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['HaveList'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
          if (arg.changes?.alertEnabled !== undefined) {
            dispatch(settingsApi.util.invalidateTags(['HaveListDefaults']));
          }
        } catch {}
      },
    }),
    bulkManageAlerts: builder.mutation<
      { affectedCount: number; mode: 'enable' | 'disable' },
      {
        minPointValue: number;
        mode: 'enable' | 'disable';
        /**
         * When true, the backend also upserts the user's
         * `defaultHaveListAlert` Settings preference to match `mode`
         * (enable → true, disable → false) in the same transaction.
         */
        autoEnableFuture?: boolean;
      }
    >({
      query: (body) => ({
        url: '/have-list/bulk-manage-alerts',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['HaveList'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(settingsApi.util.invalidateTags(['HaveListDefaults']));
        } catch {}
      },
    }),
    addSetToHaveList: builder.mutation<AddSetToHaveListResponse, AddSetToHaveListRequest>({
      query: (body) => ({
        url: '/have-list/add-set',
        method: 'POST',
        body,
        requiresAuth: true,
        timeout: 300000,
      }),
      invalidatesTags: ['HaveList'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(tradeApi.util.invalidateTags(['MatchIndex']));
        } catch {}
      },
    }),
  }),
});

export const {
  useHaveListFetchInfiniteQuery,
  useHaveListFetchPaginatedQuery,
  useHaveListQuantitySummaryQuery,
  useAddCardToHaveListMutation,
  useCardToHaveListQuantityUpdateMutation,
  useRemoveCardFromHaveListMutation,
  useUpdateHaveListCardMutation,
  useToggleTradeStatusMutation,
  useToggleFoilStatusMutation,
  useToggleAlertEnabledMutation,
  useDeleteAllHaveListItemsMutation,
  useBulkImportHaveListMutation,
  useBatchUpdateHaveListMutation,
  useLazyExportHaveListQuery,
  useAddSetToHaveListMutation,
  useBulkManageAlertsMutation,
} = haveListApi;
